<template>
  <b-col v-if="openPlan" class="pricing-info-container justify-content-center">
    <div class="d-flex flex-column">
      <!-- Plan Title -->
      <div class="title">
        <span v-if="!isSwitchingPlan">{{ openPlan.name }}</span>
        <span v-else class="shimmer"></span>
      </div>

      <!-- Plan Selector -->
      <div v-if="showPlanSwitch" class="plan-selector-block d-flex mb-3">
        <div
          class="plan-option annual-plan d-flex align-items-center justify-content-between"
          :class="{ selected: openPlan.chargeFrecuency === 'ANNUALLY' }"
          @click="selectAnnualPlan"
        >
          <span v-if="!isSwitchingPlan" class="plan-label font-weight-bold">
            {{ $t('purchase.price.info.annualPlan') }}
          </span>
          <span v-else class="plan-label font-weight-bold shimmer"></span>
          <span v-if="!isSwitchingPlan" class="discount-badge">-{{ relatedDiscount }}%</span>
          <span v-else class="discount-badge shimmer"></span>
        </div>
        <div
          class="plan-option monthly-plan d-flex align-items-center justify-content-center"
          :class="{ selected: openPlan.chargeFrecuency === 'MONTHLY' }"
          @click="selectMonthlyPlan"
        >
          <span v-if="!isSwitchingPlan" class="plan-label font-weight-bold">
            {{ $t('purchase.price.info.monthlyPlan') }}
          </span>
          <span v-else class="plan-label font-weight-bold shimmer"></span>
        </div>
      </div>

      <div class="divider"></div>

      <!-- Pricing Details -->
      <div v-if="totals.coupon || openPlan.chargeFrecuency !== 'MONTHLY'" class="pricing-details">
        <!-- Plan Name and Price -->
        <div class="d-flex justify-content-between plan-name-row">
          <div class="plan-name">
            <span v-if="!isSwitchingPlan">{{ openPlan.name }}</span>
            <span v-else class="shimmer"></span>
          </div>
          <div class="plan-price">
            <span v-if="!isSwitchingPlan">$ {{ openPlan.originalCostWithoutDiscount }}</span>
            <span v-else class="shimmer"></span>
          </div>
        </div>

        <!-- Discount Row -->
        <div
          v-if="openPlan.chargeFrecuency != 'MONTHLY'"
          class="d-flex justify-content-between align-items-center discount-row"
        >
          <div class="discount-label">
            <img
              src="https://cdn.vexels.com/img/icons/tag.svg"
              :alt="$t('purchase.price.info.encryption')"
              height="16"
              width="16"
              loading="lazy"
            />
            <span v-if="!isSwitchingPlan">
              <span v-if="openPlan.chargeFrecuency === 'ANNUALLY'">{{
                $t('purchase.price.info.annualDiscount')
              }}</span>
              <span v-else-if="openPlan.chargeFrecuency === 'LIFETIME'">{{
                $t('purchase.price.info.lifetimeDiscount')
              }}</span>
            </span>
            <span v-else class="shimmer"></span>
          </div>

          <div class="d-flex discount-values align-items-center">
            <div class="discount-percentage">
              <span v-if="!isSwitchingPlan">-{{ openPlan.discount }}%</span>
              <span v-else class="shimmer"></span>
            </div>
            <div class="discount-amount">
              <span v-if="!isSwitchingPlan"
                >-$ {{ openPlan.originalCostWithoutDiscount - openPlan.originalCost }}</span
              >
              <span v-else class="shimmer"></span>
            </div>
          </div>
        </div>

        <!-- Coupon Row -->
        <div
          v-if="totals.coupon && activeMethod === 'stripe-elements'"
          class="d-flex justify-content-between align-items-center coupon-row"
        >
          <div class="coupon-label">
            <img
              src="https://cdn.vexels.com/img/icons/tag.svg"
              :alt="$t('purchase.price.info.encryption')"
              height="16"
              width="16"
              loading="lazy"
            />
            <span>{{ $t('purchase.price.info.couponCode', { code: totals.coupon.code }) }}</span>
          </div>

          <div class="d-flex coupon-value align-items-center">
            <span class="coupon-amount">-$ {{ totals.discount }}</span>
          </div>
        </div>
      </div>

      <div v-if="totals.coupon || openPlan.chargeFrecuency !== 'MONTHLY'" class="divider"></div>

      <!-- Total Due Section -->
      <div class="flex justify-content-between">
        <div class="d-flex flex-column justify-center">
          <div class="total-due">
            <span v-if="!isSwitchingPlan">{{ $t('purchase.price.info.dueToday') }}</span>
            <span v-else class="shimmer"></span>
          </div>

          <p v-if="openPlan.hasFreeTrial && !freeTrialUsed" class="free-trial gray-text">
            <span v-if="!isSwitchingPlan">
              {{ $t('purchase.price.info.freeTrial') }}
              <span v-if="openPlan.couponCode || totals.coupon">
                <del v-if="openPlan.couponCode" class="text-danger"
                  >${{ Math.ceil(openPlan.originalCostWithoutDiscount) }}</del
                >
                <del v-else-if="totals.coupon">${{ openPlan.originalCost }}</del>
                ${{ currentPrice }}
              </span>
              <span v-else>${{ openPlan.originalCost }}</span>
            </span>
            <span v-else class="shimmer"></span>
          </p>
        </div>

        <div class="price">
          <span v-if="!isSwitchingPlan">
            <span v-if="openPlan.hasFreeTrial && !freeTrialUsed" class="amount">$0</span>
            <span v-else>
              <span v-if="openPlan.couponCode || totals.coupon">
                <span class="amount">${{ currentPrice }}</span>
              </span>
              <span v-else class="amount">${{ openPlan.originalCost }}</span>
            </span>
          </span>
          <span v-else class="shimmer"></span>
        </div>
      </div>

      <!-- Coupon Section -->
      <div v-show="activeMethod === 'stripe-elements' || openPlan.couponCode">
        <div class="divider"></div>

        <!-- Coupon Input -->
        <div v-if="activeMethod === 'stripe-elements'" class="coupon-container">
          <h2 class="coupon-title gray-text">{{ $t('purchase.price.info.couponTitle') }}</h2>
          <div
            class="input-wrapper"
            :class="{
              'is-focused': isInputFocused,
              'has-error':
                !getLoadingPayment &&
                !getLoadingCoupon &&
                !isInputFocused &&
                !noError &&
                (stripeError || (totals && !totals.coupon)) &&
                code !== ''
            }"
          >
            <input
              v-model="couponCode"
              type="text"
              :placeholder="$t('purchase.price.info.couponPlaceholder')"
              class="input-field"
              :disabled="getLoadingPayment || getLoadingCoupon"
              @focus="isInputFocused = true"
              @blur="isInputFocused = false"
              @keyup.enter="applyPromotionCode"
            />
            <Button
              class="add-button"
              color="eerie-black"
              text="white"
              :style="{
                '--custom-radius': '8px',
                '--custom-font-size': '14px'
              }"
              :enabled="!getLoadingPayment && !getLoadingCoupon && couponCode !== ''"
              @click="applyPromotionCode"
            >
              <span class="is-desktop">{{ $t('purchase.price.info.couponButton') }}</span>
              <span class="is-mobile">{{ $t('purchase.price.info.couponButtonShort') }}</span>
            </Button>
          </div>
          <div
            v-if="
              !getLoadingPayment &&
              !getLoadingCoupon &&
              !noError &&
              (stripeError || (totals && !totals.coupon)) &&
              code !== ''
            "
            class="coupon-error d-flex text-danger"
          >
            <Icon :width="14" :height="14" icon="info-error" />
            <span v-if="stripeError">{{ $t('purchase.price.info.couponErrorInvalid') }}</span>
            <span v-else-if="totals && !totals.coupon">{{
              $t('purchase.price.info.couponError')
            }}</span>
          </div>
        </div>

        <!-- Active Coupon Display -->
        <div v-if="openPlan.couponCode || totals.coupon">
          <div
            v-if="activeMethod === 'stripe-elements' && totals.coupon"
            class="coupon-section d-flex align-items-center justify-content-between"
          >
            <div class="coupon-badge grid gap-3 py-2">
              <span class="font-weight-bold">{{ totals.coupon.code }}</span>
              <Button class="remove-coupon btn text-dark p-0" @click="removePromotionCode">
                ✕
              </Button>
            </div>
          </div>
        </div>
      </div>

      <!-- Auto Renew Text -->
      <div v-if="openPlan.chargeFrecuency != 'LIFETIME'" class="auto-renew-text">
        <div class="divider"></div>
        <span v-if="!isSwitchingPlan">
          {{
            $t('purchase.price.info.autoRenew', {
              amount: openPlan.originalCost,
              frequency:
                openPlan.chargeFrecuency === 'ANNUALLY'
                  ? $t('purchase.price.info.year')
                  : $t('purchase.price.info.month')
            })
          }}
        </span>
        <span v-else class="shimmer auto-renew"></span>
      </div>

      <div>
        <slot></slot>
      </div>
    </div>
  </b-col>
</template>

<script>
import Button from '@/components/UI/Button';
import Icon from '@/components/UI/Icon';
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'PricingInfo',

  components: {
    Button,
    Icon
  },

  props: {
    activeMethod: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      isInputFocused: false,
      couponCode: '',
      noError: true,
      isCouponEnabled: true,
      showPlanSwitch: false,
      originalPlanId: null,
      relatedDiscount: null,
      isSwitchingPlan: false
    };
  },

  computed: {
    ...mapState('plans', ['openPlan']),
    ...mapState('payments', [
      'freeTrialUsed',
      'totals',
      'stripeError',
      'code',
      'updateSubscription'
    ]),
    ...mapGetters('payments', ['getLoadingPayment', 'getLoadingCoupon']),

    chargeFrequencyText() {
      const frequency = this.openPlan.chargeFrecuency;
      return frequency === 'ANNUALLY'
        ? this.$t('purchase.price.info.billedAnnually')
        : frequency === 'MONTHLY' || frequency === 'MONTHLY_ANNUAL_COMMITMENT'
        ? this.$t('purchase.price.info.billedMonthly')
        : frequency === 'LIFETIME'
        ? this.$t('purchase.price.info.billedOneTime')
        : '';
    },

    currentPrice() {
      if (this.activeMethod === 'stripe-elements' && Object.keys(this.totals).length > 0)
        return this.totals.total;
      return this.openPlan.originalCost;
    }
  },

  watch: {
    stripeError() {
      if (this.noError && this.stripeError) {
        this.noError = false;
      }
    },
    totals() {
      if (this.noError && this.totals && !this.totals.coupon) {
        this.noError = false;
      }
    }
  },

  mounted() {
    this.showPlanSwitch =
      this.openPlan && this.openPlan.chargeFrecuency === 'MONTHLY' && this.openPlan.relatedPlan;
    this.originalPlanId = this.openPlan.id;
    this.relatedDiscount = this.openPlan?.relatedPlan?.discount;
  },

  methods: {
    needHelp() {
      try {
        window.$crisp.push(['do', 'chat:open']);
      } catch (error) {
        console.log(error);
      }
    },

    applyPromotionCode() {
      this.isInputFocused = false;
      this.$store.commit('payments/SET_LOADING_COUPON', true);
      this.$store.commit('payments/SET_CODE', this.couponCode);
      this.$store.commit('payments/SET_UPDATE_SUBSCRIPTION', true);
      this.couponCode = '';
    },

    removePromotionCode() {
      this.$store.commit('payments/SET_CODE', '');
      this.$store.commit('payments/SET_UPDATE_SUBSCRIPTION', true);
      this.applyPromotionCode();
    },

    async selectAnnualPlan() {
      if (this.openPlan.chargeFrecuency !== 'ANNUALLY') {
        this.isSwitchingPlan = true;
        try {
          this.$dataLayerPush('switch_plan_annual', {
            planSwitch: {
              fromPlan: this.openPlan.id,
              toPlan: this.openPlan.relatedPlan?.id,
              fromFrequency: 'MONTHLY',
              toFrequency: 'ANNUALLY',
              fromPrice: this.openPlan.originalCost,
              toPrice: this.openPlan.relatedPlan?.originalCost
            }
          });
          const plan = await this.$store.dispatch('plans/GET_PLAN', this.openPlan.relatedPlan.id);
          if (plan) {
            this.$store.commit('plans/SET_OPEN_PLAN', plan);
            this.$store.commit('payments/SET_UPDATE_SUBSCRIPTION', true);
          }
        } finally {
          this.isSwitchingPlan = false;
        }
      }
    },

    async selectMonthlyPlan() {
      this.isSwitchingPlan = true;
      try {
        this.$dataLayerPush('switch_plan_monthly', {
          planSwitch: {
            fromPlan: this.openPlan.id,
            toPlan: this.originalPlanId,
            fromFrequency: 'ANNUALLY',
            toFrequency: 'MONTHLY',
            fromPrice: this.openPlan.originalCost,
            toPrice: this.openPlan.relatedPlan?.originalCost
          }
        });
        const plan = await this.$store.dispatch('plans/GET_PLAN', this.originalPlanId);
        if (plan) {
          this.$store.commit('plans/SET_OPEN_PLAN', plan);
          this.$store.commit('payments/SET_UPDATE_SUBSCRIPTION', true);
        }
      } finally {
        this.isSwitchingPlan = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
// Layout
.pricing-info-container {
  padding: 17px 27px;

  @include lg {
    padding: 30px;
  }
}

// Typography
.title {
  font-weight: 600;
  font-size: 24px;
}

.subtitle {
  font-size: 13px;
}

.frequency {
  font-size: 12px;
  margin: 15px 0 20px;
  color: #515151;
}

.contact-text {
  font-size: 13px;
}

.gray-text {
  color: #515151;
}

.terms-and-cond {
  font-size: 12px;
}

// Pricing Elements
.price {
  font-size: 15px;
  text-align: right;
}

.total-due {
  font-size: 14px;
  color: #696969;
  font-weight: 700;
  display: flex;
  align-items: center;
}

.amount {
  font-size: 14px;
  font-weight: 700;
  color: #696969;
}

.currency {
  font-size: 12px;
}

.free-trial {
  font-size: 13px;
  margin-bottom: 0;
}

// Dividers
.divider {
  border-top: 2px solid #f1f1f1;
  margin: 21px 0;
}

// Coupon Section
.coupon-container {
  width: 100%;
  transition: all 0.15s ease;
}

.coupon-title {
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 8px;
}

.input-wrapper {
  display: flex;
  align-items: center;
  background: #f1f1f1;
  border-radius: 12px;
  padding: 0.5rem;
  margin: 0 auto 16px;

  &.is-focused {
    box-shadow: 0 0 0 3px #007bff40, 0 0 0 1px #007aff;
  }

  &.has-error {
    box-shadow: 0 0 0 2px #df1b41;
  }
}

.input-field {
  display: flex;
  width: 100%;
  border: none;
  background: transparent;
  padding: 8px;
  font-size: 15px;
  color: #30313d;
  font-family: inherit;
  box-sizing: border-box;

  &::placeholder {
    color: #6c6d79;
  }

  &:focus {
    outline: none;
  }
}

.add-button {
  width: 100%;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  justify-content: flex-end;

  &:disabled {
    background: #3f80f166;
    cursor: not-allowed;
  }
}

// Responsive Elements
.is-desktop {
  display: block;
}

.is-mobile {
  display: none;
}

@include lg {
  .is-desktop {
    display: none;
  }

  .is-mobile {
    display: block;
  }
}

// Coupon Badge
.coupon-section {
  margin: 0 auto 20px;
  font-size: 15px;
  font-weight: 400;
}

.coupon-badge {
  padding: 0 10px;
  border: 2px solid #1d1d1d33;
  border-radius: 5px;
}

.remove-coupon {
  font-size: 1em;
  line-height: 1em;
  padding: 0;

  &:hover {
    opacity: 0.7;
    text-decoration: none;
  }

  &:focus {
    outline: none;
  }
}

.coupon-error {
  font-size: 12px;
  margin-bottom: 16px;
  gap: 5px;
}

// Auto Renew Text
.auto-renew-text {
  font-weight: 500;
  font-size: 12px;
  line-height: 1;
  letter-spacing: 0%;
  text-align: center;
  vertical-align: middle;
  color: #696969;
}

// Pricing Details
.pricing-details {
  display: grid;
  gap: 10px;
  line-height: 1;
  font-size: 14px;
  font-weight: 500;
  color: #696969;

  .discount-label {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .discount-values,
  .coupon-value {
    gap: 10px;
  }

  .discount-percentage,
  .coupon-percentage {
    align-items: center;
    background: rgba(56, 178, 122, 0.1);
    border-radius: 2px;
    color: #329f6c;
    display: flex;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    gap: 10px;
    justify-content: center;
    line-height: 14px;
    padding: 2px 6px;
  }

  .discount-amount,
  .coupon-amount {
    color: #329f6c;
  }
}

// Plan Selector
.plan-selector-block {
  display: flex;
  width: 100%;
  background: #fff;
  margin: 21px 0 24px;
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
  overflow: hidden;

  .plan-option {
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 16px 18px;
    background: #fff;
    cursor: pointer;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    text-transform: capitalize;
    color: #1d1d1d;
    box-shadow: none;
    transition: background 0.2s, color 0.2s, opacity 0.2s, border-color 0.2s;
    opacity: 1;
    border-radius: 0;
    gap: 8px;
    border: 1px solid #cfcfcf;

    &.selected {
      background: rgba(63, 128, 241, 0.1);
      color: #3f80f1;
      border: 1px solid rgba(63, 128, 241, 0.2);
      box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
      z-index: 2;
    }

    &:first-child {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
    }

    &:last-child {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
      align-items: flex-end;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }

  .plan-label {
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    text-transform: capitalize;
    color: inherit;
  }

  .discount-badge {
    display: flex;
    padding: 2px 6px;
    justify-content: center;
    align-items: center;
    gap: 10px;
    border-radius: 2px;
    background: #3f80f1;
    font-size: 11px;
    font-style: normal;
    font-weight: 500;
    line-height: 14px;
    color: #fff;
    text-align: right;
    margin-left: auto;
  }
}

// Loading States
.shimmer {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  height: 20px;
  width: 80px;
  display: inline-block;

  &.title {
    width: 200px;
    height: 24px;
  }

  &.price {
    width: 60px;
    height: 14px;
  }

  &.discount {
    width: 100px;
    height: 14px;
  }

  &.auto-renew {
    width: 100%;
    height: 12px;
    margin: 0 auto;
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
</style>
